import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth";
import { apiFetch } from "@/lib/api";
import { keys } from "@/hooks/keys";
import { API_BASE } from "@/lib/utils";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface OrgMember {
  id: string;
  email: string;
  name: string;
  role: string;
  createdAt: string;
}

export interface Team {
  id: string;
  name: string;
  memberCount: number;
  createdAt: string;
}

// ─── Queries ──────────────────────────────────────────────────────────────────

export function useUsers() {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;

  return useQuery({
    queryKey: keys.users.list(orgId),
    queryFn: () => apiFetch<OrgMember[]>(`${API_BASE}/api/v1/users`),
    enabled: !!orgId,
  });
}

export interface PendingMember {
  id: string;
  userId: string;
  orgId: string;
  role: string;
  email: string;
  name: string;
  createdAt: string;
}

export function usePendingMembers() {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;
  const role = userContext?.role;
  const isAdmin = role === "admin" || role === "owner" || role === "platform_admin";

  return useQuery({
    queryKey: keys.users.pending(orgId),
    queryFn: () => apiFetch<PendingMember[]>(`${API_BASE}/api/v1/users/pending`),
    enabled: !!orgId && isAdmin,
    refetchInterval: 60_000, // poll every 60s for new requests
  });
}
